import { Injectable } from '@angular/core';
import { DataObject } from '../../interfaces/data-objects';
import { TransactionTypes, TransactionType } from '../../models/transactions-enum';
@Injectable({
  providedIn: 'root'
})
export class TransactionValidationService {
  constructor() { }

  isPageValid(pageSlug: string, validationResult: DataObject, userCountry: string, pageLink: string) {

    if (validationResult.status === 404) {
      return false;
    }

    if (validationResult.error) {
      validationResult = validationResult.error;
    }

    const transactionType = this.getTransactionTypeBasedOnPageLink(pageLink) as TransactionType;

    switch (pageSlug) {
      case 'property':
      case 'properties':
        return this.isPropertiesPageValid(validationResult, userCountry, transactionType);
      case 'parties':
        return this.isPartiesPageValid(validationResult);
      case 'fees-split':
        return this.isFeeSplitPageValid(validationResult, userCountry);
      case 'financials':
        return this.isFinancialsPageValid(validationResult);
      case 'consultingdetails':
        return (this.isPropertiesPageValid(validationResult, userCountry, transactionType) && this.isPartiesPageValid(validationResult));
      case 'createcustomer':
        return this.isCreateCustomerPageValid(validationResult);
      case 'disbursementdetails':
        return this.isDisbursementPageValid(validationResult, userCountry);
      case 'updatecustomer':
        return this.isUpdateCustomerPageValid(validationResult);
      case 'engagementdetails':
        return this.isDSFEngagementDetailsValid(validationResult);
      default:
        return false;
    }
  }

  isPropertiesPageValid(validationResult: DataObject, userCountry: string, transactionType: TransactionType) {
    return ![
      'execution_date',
      'transaction_type',
      'asset_type',
      'start_date',
      'end_date',
      'break_date',
      'review_cycle',
      'properties',
      'rent_review_details',
      'managed_by_cbre',
      'spacetypes',
      'property_grid_id',
      'option_term_details',
      'absorbtion_date',
      'early_termination_date',
      'portfolio_name',
      'bill_to_email_address_check',
      'sale_completion_date',
      'property_base_id',
      ...(['JPN'].includes(userCountry) && ![TransactionTypes.CONSULTING].includes(transactionType) ? ['transaction_date'] : []),
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  isPartiesPageValid(validationResult: DataObject) {
    return ![
      'represented_party',
      'represented_contact',
      'represented_contact_email',
      'represented_address',
      'ip_metro_represented_name',
      'purchase_order',
      'bill_to_address',
      'bill_to_contact',
      'bill_to_email_address',
      'bill_to_party',
      'bill_to_vat',
      'counter_party_contact',
      'counter_party_name',
      'counter_party_email',
      'counter_party_equity_source',
      'represented_equity_source',
      'trust_receipt_date',
      'counter_party_rep',
      'counter_party',
      'intermediary',
      'other_counter_party_name',
      'supplier_type',
      'tenant_retail_category',
      'represented_contact_name',
      'represented_contact_email_check',
      'bill_to_email_address_check',
      'edp_represented_party',
      'edp_other_party',
      'edp_counter_party',
      'mkt_trust_receipt_date',
      'mkt_receipt_number',
      'mkt_receipt_amount',
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  isFeeSplitPageValid(validationResult: DataObject, userCountry: string) {
    return ![
      'gst_rate',
      'billing_entity',
      'billing_currency',
      'rate_type',
      'service_location',
      'fee_service_type',
      'expense_service_type',
      'expenses',
      'fee_splits',
      'fee_period',
      'fee_components',
      'fee_influencers',
      'fee_contracts',
      'fee_notes',
      'notes',
      'amount_of_disbursement',
      'fee_splits_originator',
      'fee_splits_primary_employee',
      'file_upload',
      'additional_fees',
      'lease_transaction_amount',
      'conjunctional_agent_fee_amount',
      'cess_code',
      'subfeetype',
      'billing_office',
      'place_of_supply',
      'pre-golive-cross-entity',
      'tax_rate_code',
      'office',
      'SHIP_TO_CUST_ID',
      'ship_to_cust_id',
      ... (!['IND', 'CHN', 'TWN'].includes(userCountry) ? ['payment_due_date'] : []),
      ... (['IND', 'JPN', 'AUS', 'HKG'].includes(userCountry) ? ['payment_terms'] : []),
      'execution_date',
      'transaction_date',
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  isInvoiceDetailsPageValid(validationResult: DataObject) {
    return ![
      'invoice_language',
      'payment_terms',
      'affiliate_code',
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  isFinancialsPageValid(validationResult: DataObject) {
    return ![
      'opportunity_id',
      'deal_check_list',
      'approver',
      'executive_approver1',
      'executive_approver2'
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  isCreateCustomerPageValid(validationResult: DataObject) {
    return ![
      'addresses',
      'company_name',
      'company_type',
      'dedicate_client_name',
      'business_number',
      'contact_person_name',
      'fax',
      'email',
      'client_abn',
      'submit',
      'detail',
      'company_name1(japanese)',
      'approval_documents',
      'goods_services_tax_identification_number',
      'client_vat_registration_number',
      'vat_registration_number',
      'client_cin',
      'client_tan',
      'client_email',
      'aml_checklist',
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  isDisbursementPageValid(validationResult: DataObject, userCountry: string) {
    const newArr = [
      'represented_party',
      'represented_address',
      'represented_contact_name',
      'represented_contact_email_check',
      'approver',
      'expenses',
      'purchase_order',
      'transaction_date',
    ];
    if (userCountry === 'HKG' || userCountry === 'CHN' || userCountry === 'TWN') {
      newArr.push('deal_check_list');
      newArr.push('file_upload');
    }
    if (!['IND', 'CHN', 'TWN'].includes(userCountry)) {
      newArr.push('payment_due_date');
    }
    if (['AUS', 'NZL'].includes(userCountry)) {
      newArr.push('office');
    }
    return !newArr.some((element, index, array) => {
      return validationResult[element];
    });
  }

  isUpdateCustomerPageValid(validationResult: DataObject) {
    return ![
      'country',
      'company_name',
      'client_abn',
      'submit',
      'cust_id',
      'detail',
      'addresses',
      'company_name1(japanese)',
      'prefecture',
      'goods_services_tax_identification_number',
      'client_vat_registration_number',
      'vat_registration_number'
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  isDSFEngagementDetailsValid(validationResult: DataObject) {
    return ![
      'settlement_date',
      'dsf_transaction_type',
      'deal_type',
      'involved_in_consultation',
      'properties',
      'subtype',
      'managed_by_cbre'
    ].some((element, index, array) => {
      return validationResult[element];
    });
  }

  getTransactionTypeBasedOnPageLink(pageLink: string): TransactionType | null {
    let transactionType: TransactionType | null = null;
    for (const key in TransactionTypes) {
      if (pageLink.includes(`/${TransactionTypes[key as keyof typeof TransactionTypes]}/`)) {
        transactionType = TransactionTypes[key as keyof typeof TransactionTypes] as TransactionType;
      }
    }
    return transactionType;
  }
}
